import { useContext, useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { IntegrationFilters } from '@components'
import {
  FiltersState,
  IntegrationFilter,
  RequiredDateFilterDefault,
} from '@components/integration-filters/components'
import { api } from 'api'
import { templateCopyContext } from '../../../../context'

const FullWidth = styled.div`
  width: 100%;
`

interface Props {
  integrationType: string
  integrationAssignment: any
  dataTypeFilter?: string
  requiredDateFilterDefaults?: RequiredDateFilterDefault[]
  previousYearsRestriction?: number
}

export const IntegrationFiltersManager = ({
  integrationType,
  integrationAssignment,
  dataTypeFilter,
  requiredDateFilterDefaults,
  previousYearsRestriction,
}: Props) => {
  const { onChange, onLoadIntegration } = useContext<any>(templateCopyContext)

  const { data: filterOptions = [], isLoading: loadingFilters } = useQuery({
    queryKey: ['integrations', integrationType, 'filters'],
    queryFn: async () => {
      const response = await api.get<IntegrationFilter[]>(
        `/api/v1/integrations/${integrationType}/filters`,
      )

      return response.data
    },
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 60,
  })

  const dateTypeFilters = useMemo(
    () =>
      !dataTypeFilter ? filterOptions : filterOptions.filter(f => f.dataType === dataTypeFilter),
    [filterOptions, dataTypeFilter],
  )

  const filtersState = useMemo((): FiltersState => {
    if (!integrationAssignment.connector_id) {
      return {
        state: 'waiting_on_account',
        message: 'Select an account under "Account Selection" to view filters',
      }
    }

    if (loadingFilters) {
      return {
        state: 'loading',
        message: 'Loading account specific filters',
      }
    }

    return {
      state: 'ready',
      message: '',
    }
  }, [loadingFilters])

  const dataReset =
    integrationAssignment.created_at !== undefined && !integrationAssignment.integration_data_reset

  const getAssignableFiltersByDataType = useCallback(
    filters =>
      filters.filter(f =>
        dateTypeFilters.find(af => af.id == f.integrationFilterId && af.dataType == dataTypeFilter),
      ),
    [dateTypeFilters],
  )

  const onFiltersChange = useCallback(
    value => {
      onLoadIntegration(old => ({
        ...JSON.parse(JSON.stringify(old)),
        [integrationAssignment.id]: {
          ...old[integrationAssignment.id],
          filters: getAssignableFiltersByDataType(value),
        },
      }))
    },
    [integrationAssignment, getAssignableFiltersByDataType, onChange],
  )

  const onDataResetChange = useCallback(
    (value, reset) => {
      onChange(old => ({
        ...JSON.parse(JSON.stringify(old)),
        [integrationAssignment.id]: {
          ...old[integrationAssignment.id],
          filters: getAssignableFiltersByDataType(value),
          integration_data_reset: reset,
        },
      }))
    },
    [integrationAssignment, getAssignableFiltersByDataType, onChange],
  )

  return (
    <FullWidth>
      <IntegrationFilters
        connectorId={integrationAssignment.connector_id}
        filters={integrationAssignment.filters || []}
        integrationFilterOptions={dateTypeFilters}
        onFiltersChange={onFiltersChange}
        filtersState={filtersState}
        onDataResetChange={onDataResetChange}
        requiredDateFilterDefaults={requiredDateFilterDefaults}
        confirmDataReset={dataReset}
        previousYearsRestriction={previousYearsRestriction}
        integrationSlug={integrationAssignment?.slug}
      />
    </FullWidth>
  )
}
